import { useEffect, useMemo } from "react";
import { Building2, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { useCommandPalette } from "@/components/domain/CommandPaletteProvider";
import type { CommandAction } from "@/components/domain/CommandPalette";
import { useRankedCustomers } from "@/hooks/useRankedCustomers";
import { isSuperadminRole, useAuth } from "@/lib/auth/AuthContext";

// Haengt Kunden-bezogene Actions in die CommandPalette:
//   - "Neuer Kunde" (Aktion)
//   - fuer jeden Kunden aus dem Ranked-List-Response einen direkten Jump
//
// Die Seite «Kunden» selbst steht seit NAV-4 unter «Gehe zu» (Provider, aus
// der Leiste) — hier nicht ein zweites Mal (Prinzip 1).
//
// Liste ist bereits ranked (recent > attention > active > archive), damit
// sind die relevantesten Kunden oben — konsistent mit Prinzip "Jede Liste
// hat eine Ordnung".

const BUNDLE_TOKEN = "customers";
const MAX_CUSTOMER_ITEMS = 40;

export function useCustomerPaletteActions(): void {
  const navigate = useNavigate();
  const { registerActions } = useCommandPalette();
  const { user } = useAuth();
  // Dieselbe Schranke wie das Business-Gate (RequireOperatingRole): der
  // UN-vertretene Superadmin bekommt keine Kunden-Aktion — die Route wuerde
  // ihn auf Home umleiten. Vertritt er, ist `role` die des Vertretenen.
  const operativ = !!user && !isSuperadminRole(user.role);
  const { data } = useRankedCustomers();

  const actions = useMemo<readonly CommandAction[]>(() => {
    if (!operativ) return [];
    const list: CommandAction[] = [
      {
        id: "customers:new",
        label: "Neuer Kunde",
        group: "Aktionen",
        icon: Plus,
        onSelect: () => {
          void navigate("/kunden/neu");
        },
      },
    ];

    if (data) {
      const ordered = [
        ...data.recent,
        ...data.attention,
        ...data.active,
        ...data.archive,
      ].slice(0, MAX_CUSTOMER_ITEMS);
      for (const c of ordered) {
        list.push({
          id: `customer:${c.id}`,
          label: c.displayName,
          group: "Kunden",
          icon: Building2,
          onSelect: () => {
            void navigate(`/kunden/${c.id}`);
          },
        });
      }
    }

    return list;
  }, [data, navigate, operativ]);

  useEffect(() => {
    return registerActions(BUNDLE_TOKEN, actions);
  }, [registerActions, actions]);
}
